'use client';

import { usePathname } from 'next/navigation';

import type { ComponentPropsWithoutRef, ComponentType, ReactNode} from 'react';
import { useMemo } from 'react';

import classNames from 'classnames';

import Amount from '@uikit/icons/Amount';
import { GoldenStar } from '@uikit/icons/GoldenStar';
import Label from '@uikit/icons/Label';
import { Link as LinkIcon } from '@uikit/icons/Link';
import { MsqToken } from '@uikit/icons/MsqToken';

import type { HotOffer } from '@shared/api/types/hotOffers';
import NextLink from '@shared/common/components/NextLink';
import { HOT_OFFER_TYPE } from '@shared/constants';
import { truncateDecimals } from '@shared/helpers/formatters';
import useCheckRequiredSelfData from '@shared/hooks/useCheckRequiredSelfData';

import { useSqrTokenPriceQuery } from '@query/token';

import { Flame } from '@ui/uikit/components/icons/poly/Flame';
import { formatAmountCompact } from '@ui/uikit/utils';

export interface HotOfferStatsProps extends ComponentPropsWithoutRef<'div'> {
  offer: HotOffer;
}

interface HotOfferStatItem {
  id: string;
  icon: ReactNode;
  content: ReactNode;
}

export const USDT_SQR_EXCH_CURRENCY = 'USDT-SQR-EXCH';

const OfferAmountSqrTokenIcon = ({ className }: { className?: string }) => (
  <MsqToken className={className} />
);
const OfferAmountAnyTokenIcon = ({ className }: { className?: string }) => (
  <Amount className={classNames('text-secondary-700', className)} />
);
const ClaimLimitIcon = ({ className }: { className?: string }) => (
  <Flame className={className} />
);
const PercentageIcon = ({ className }: { className?: string }) => (
  <Label className={classNames('text-primary-600', className)} />
);
const PoolIcon = ({ className }: { className?: string }) => (
  <Amount className={classNames('text-secondary-700', className)} />
);

export const useHotOfferStats = (offer?: HotOffer) => {
  const pathname = usePathname();
  const { check } = useCheckRequiredSelfData();

  const isUserPage = pathname.includes('/user/');

  const {
    data: sqrTokenPrice,
    isLoading: isSqrTokenPriceLoading,
    isError: isSqrTokenPriceError,
  } = useSqrTokenPriceQuery({
    enabled: offer?.attributes.offerCurrency === USDT_SQR_EXCH_CURRENCY,
  });

  return useMemo(() => {
    if (!offer) {
      return [];
    }

    const {
      type,
      attributes: {
        offerAmount,
        cashBackPercentage,
        poolAmount,
        usedPoolAmount,
        offerCurrency,
        claimsLimit,
        claimsCounted,
        rewardPoolId,
      } = {},
    } = offer;

    const isRewardPool = Boolean(rewardPoolId);

    const offerStats: {
      id: string;
      Icon?: ComponentType<{ className?: string }>;
      icon: ReactNode;
      content: ReactNode;
    }[] = [];

    if (isRewardPool) {
      const rewardPoolLinkLabel = offer?.included?.find((value) => value.id === rewardPoolId);

      offerStats.push({
        id: 'reward-pool: ' + offerAmount + offerCurrency,
        Icon: GoldenStar,
        icon: <GoldenStar className="icon-sm" />,
        content: (
          <span className="whitespace-nowrap">
            {offerAmount} {offerCurrency}
          </span>
        ),
      });

      offerStats.push({
        id: `reward-pool-${rewardPoolId}`,
        icon: null,
        content: (
          <div className="max-w-[170px] flex items-center justify-end gap-1">
            <NextLink
              className="text-primary-600 truncate whitespace-nowrap overflow-hidden text-ellipsis font-normal"
              href={`/reward-pools/${rewardPoolId}`}
              onClick={(event) => {
                if (!check({ auth: true })) {
                  event.preventDefault();

                  return;
                }
              }}
            >
              {rewardPoolLinkLabel?.attributes?.name || 'Reward Pool'}
            </NextLink>
            <LinkIcon
              className={classNames('text-primary-600', isUserPage ? 'icon-2xs' : 'icon-xs')}
            />
          </div>
        ),
      });

      return offerStats;
    }

    if (([HOT_OFFER_TYPE.OFFER, HOT_OFFER_TYPE.SO_QUEST_OFFER] as string[]).includes(type)) {
      if (offerCurrency === USDT_SQR_EXCH_CURRENCY) {
        let content: ReactNode = '';

        if (isSqrTokenPriceLoading) {
          content = 'Loading...';
        } else if (sqrTokenPrice && offerAmount) {
          content = (
            <>
              <span>
                {+truncateDecimals(Number(offerAmount) / sqrTokenPrice.attributes.price, 2)} SQR
              </span>
              <span className="tag text-inherit bg-neutral-100 text-neutral-500">
                ≈ {'$'}
                {offerAmount}
              </span>
            </>
          );
        } else if (isSqrTokenPriceError) {
          content = 'Error';
        }

        offerStats.push({
          id: 'offer-amount',
          Icon: OfferAmountSqrTokenIcon,
          icon: <OfferAmountSqrTokenIcon className="icon-sm" />,
          content,
        });
      } else {
        offerStats.push({
          id: 'offer-amount',
          Icon: OfferAmountAnyTokenIcon,
          icon: <OfferAmountAnyTokenIcon className="icon-sm" />,
          content: `${offerAmount || 0} ${offerCurrency ? offerCurrency : ''}`,
        });
      }

      if (claimsLimit !== null) {
        offerStats.push({
          id: 'claim-limit',
          icon: <ClaimLimitIcon className="icon-sm" />,
          Icon: ClaimLimitIcon,
          content: `${Math.min(claimsCounted || 0, claimsLimit || 0)}/${claimsLimit || 0}`,
        });
      }

      return offerStats;
    }

    if (type === HOT_OFFER_TYPE.BINANCE) {
      return [
        {
          id: 'percentage',
          icon: <PercentageIcon className="icon-sm" />,
          Icon: PercentageIcon,
          content: `${cashBackPercentage || 0}%`,
        },
        {
          id: 'pool',
          icon: <PoolIcon className="icon-sm" />,
          Icon: PoolIcon,
          content: `${formatAmountCompact(
            Math.min(usedPoolAmount || 0, poolAmount || 0),
          )}/${formatAmountCompact(poolAmount || 0)}`,
        },
      ];
    }

    return [];
  }, [offer, isUserPage, check, isSqrTokenPriceLoading, sqrTokenPrice, isSqrTokenPriceError]);
};

export default function HotOfferStats({ className, offer }: HotOfferStatsProps) {
  const stats: HotOfferStatItem[] = useHotOfferStats(offer);

  if (!stats.length) {
    return null;
  }

  return (
    <ul className={classNames('w-full flex items-center justify-between gap-3', className)}>
      {stats.map(({ icon, content, id }) => (
        <li
          key={id}
          className="sm:text-sm md:text-lg font-semibold inline-flex items-center gap-2 overflow-hidden text-ellipsis whitespace-nowrap"
          data-qa="hot-offer-container-stat-item"
        >
          {icon} {content}
        </li>
      ))}
    </ul>
  );
}
